import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  Signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CapturumButtonComponent } from '@capturum/ui/button';
import { CapturumDynamicFilterLayoutComponent, DynamicFilterComponent } from '@capturum/ui/dynamic-filters';
import {
  DateTimeRangeFilterConfigItem,
  DateTimeRangeValue,
} from '@core/dynamic-filters/interfaces/date-time-range-filter.interface';
import { GenericFormGroup } from '@core/interfaces/form.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DateTimeRangeService } from '@shared/services/date-time-range.service';
import { addDays, format } from 'date-fns';
import { DatePicker } from 'primeng/datepicker';
import { Popover, PopoverModule } from 'primeng/popover';
import { DateRangeArrayPipe } from './pipes/date-range-array.pipe';

@Component({
  selector: 'app-date-time-range-filter',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DatePicker,
    PopoverModule,
    CapturumDynamicFilterLayoutComponent,
    CapturumButtonComponent,
    TranslateModule,
    DatePipe,
    DateRangeArrayPipe,
  ],
  providers: [DateTimeRangeService],
  templateUrl: './date-time-range-filter.component.html',
  styleUrl: './date-time-range-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class DateTimeRangeFilterComponent implements DynamicFilterComponent<DateTimeRangeValue> {
  readonly dateTimeRangePicker = viewChild<Popover>('dateTimeRangePicker');
  readonly filterConfig = input<DateTimeRangeFilterConfigItem, DateTimeRangeFilterConfigItem>(undefined, {
    transform: (value) => {
      const selectionMode = value.selectionMode || 'single';
      const maxDateRangeInDays = selectionMode === 'range' ? value.maxDateRangeInDays || 2 : null;

      return {
        ...value,
        selectionMode,
        maxDateRangeInDays,
      };
    },
  });

  updateValue = output<DateTimeRangeValue>();
  formControl = input<FormControl<DateTimeRangeValue>>(new FormControl<DateTimeRangeValue>(null));
  formGroup: FormGroup<GenericFormGroup<DateTimeRangeValue>>;
  minDate = computed(() => {
    return this.filterConfig().minDate || null;
  });

  maxDate = linkedSignal(() => {
    return this.filterConfig().maxDate || null;
  });

  readonly #formBuilder = inject(FormBuilder);
  readonly #dateTimeRangeService = inject(DateTimeRangeService);
  #formChanges: Signal<unknown>;

  /** `yyyy-MM-dd` strings for the native date inputs, so the calendar and the inputs stay in sync. */
  dateInputs: Signal<{ from: string; to: string }>;
  minInput = computed(() => {
    return this.#toInputValue(this.minDate());
  });

  maxInput = computed(() => {
    return this.#toInputValue(this.maxDate());
  });

  constructor() {
    this.formGroup = this.#formBuilder.group<GenericFormGroup<DateTimeRangeValue>>({
      date: this.#formBuilder.control(null),
      from: this.#formBuilder.control(null),
      to: this.#formBuilder.control(null),
    });

    this.#formChanges = toSignal(this.formGroup.valueChanges, { initialValue: null });
    this.dateInputs = computed(() => {
      this.#formChanges(); // the form is the source of truth; this read is just the trigger

      const value = this.dateControl.value;
      const [from, to] = Array.isArray(value) ? value : [value, null];

      return { from: this.#toInputValue(from), to: this.#toInputValue(to) };
    });
  }

  get dateControl(): FormControl<Date | string | [Date, Date] | [string, string] | null> {
    return this.formGroup.get('date') as FormControl<Date | string | null>;
  }

  checkFromToTime(name: string): void {
    this.#dateTimeRangeService.checkFromToTime(name, this.formGroup);
  }

  setValue(value: DateTimeRangeValue): void {
    let dateValue = value.date;
    const rangeMode = this.filterConfig().selectionMode === 'range';

    if (!Array.isArray(value.date) && value.date && rangeMode) {
      dateValue = [value.date, value.date];
    }

    const normalizedValue = {
      ...value,
      date: dateValue,
    };

    this.formGroup.setValue(normalizedValue);
    this.formControl()?.setValue(normalizedValue);
  }

  reset(): void {
    const resetValue = this.filterConfig().resetValue || {
      date: null,
      from: null,
      to: null,
    };

    this.formGroup.setValue(resetValue);
    this.formControl().setValue(resetValue);
    this.updateValue.emit(this.formGroup.value);
  }

  updateView(): void {
    return void 0;
  }

  toggle(event: MouseEvent): void {
    this.#dateTimeRangeService.toggleDatePicker(event, this.dateTimeRangePicker());
  }

  onDateSelect(): void {
    const dateValue = (this.dateControl.value as [Date, Date]) || [null, null];

    this.#dateTimeRangeService.onDateSelect(
      this.filterConfig().selectionMode,
      dateValue,
      this.filterConfig().maxDateRangeInDays,
      this.maxDate,
    );
  }

  apply(): void {
    const value = this.formGroup.value;
    const normalizedValue = this.#dateTimeRangeService.normalizeValue(value, this.filterConfig().selectionMode);

    this.updateValue.emit(normalizedValue);
    this.formControl().setValue(normalizedValue);
    this.dateTimeRangePicker().hide();
  }

  resetTime(): void {
    this.#dateTimeRangeService.resetTime(this.formGroup);
  }

  /** Typed date from a native `<input type="date">`; `index` is 0 for the from-date, 1 for the to-date. */
  onDateInput(index: 0 | 1, event: Event): void {
    const typed = this.#fromInputValue((event.target as HTMLInputElement).value);

    if (this.filterConfig().selectionMode !== 'range') {
      this.dateControl.setValue(typed);

      return;
    }

    const current = this.dateControl.value;
    const [from, to] = Array.isArray(current) ? current.map(this.#toDate) : [this.#toDate(current), null];
    const range: [Date, Date] = index === 0 ? [typed, to] : [from, typed];
    const limit = this.filterConfig().maxDateRangeInDays;

    // `max` on the input only flags an out-of-range value, it doesn't stop the user typing one
    if (limit && range[0] && range[1] && range[1] > addDays(range[0], limit)) {
      if (index === 0) {
        range[1] = addDays(range[0], limit);
      } else {
        range[0] = addDays(range[1], -limit);
      }
    }

    this.dateControl.setValue(range);
    this.onDateSelect();
  }

  #toDate = (value: Date | string): Date | null => {
    if (!value) {
      return null;
    }

    return value instanceof Date ? value : new Date(value);
  };

  #toInputValue(value: Date | string): string {
    const date = this.#toDate(value);

    return date ? format(date, 'yyyy-MM-dd') : '';
  }

  // Parsed by hand: `valueAsDate` is UTC midnight, which reads as the previous day east of Greenwich.
  #fromInputValue(value: string): Date | null {
    const [year, month, day] = value.split('-').map(Number);

    return value ? new Date(year, month - 1, day) : null;
  }
}
